import React, { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { heroData } from '../data/mock';
import { Button } from './ui/button';
import { ArrowRight, Play } from 'lucide-react';
import gsap from 'gsap';

export default function Hero() {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start']
  });

  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  useEffect(() => {
    const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

    // Masked line-by-line reveal
    tl.from('.hero-line', {
      opacity: 0,
      y: 100,
      rotateX: -90,
      transformOrigin: 'top center',
      stagger: 0.15,
      duration: 1.2,
      delay: 0.5
    });

    tl.from('.hero-description', {
      opacity: 0,
      y: 30,
      duration: 0.8
    }, '-=0.4');

    tl.from('.hero-cta', {
      opacity: 0,
      y: 30,
      scale: 0.95,
      stagger: 0.1,
      duration: 0.6
    }, '-=0.4');

    // Floating animation for globe elements
    gsap.to('.floating-element', {
      y: '+=30',
      rotation: '+=10',
      duration: 3,
      ease: 'power1.inOut',
      stagger: 0.2,
      repeat: -1,
      yoyo: true
    });
  }, []);

  return (
    <div ref={containerRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Gradient Orbs */}
      <div className="absolute top-20 left-20 w-96 h-96 bg-[#2A4A85]/20 rounded-full blur-3xl floating-element" />
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#4A6FA5]/20 rounded-full blur-3xl floating-element" />
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#4A6FA5]/10 rounded-full blur-3xl" />

      {/* Grid Background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(to right, #4A6FA5 1px, transparent 1px),
            linear-gradient(to bottom, #4A6FA5 1px, transparent 1px)
          `,
          backgroundSize: '80px 80px',
          maskImage: 'radial-gradient(ellipse at center, black 0%, transparent 70%)'
        }} />
      </div>

      <motion.div 
        className="relative z-10 container mx-auto px-6 text-center"
        style={{ y, opacity }}
      >
        {/* Company Badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 backdrop-blur-xl border border-white/10 mb-8"
        >
          <div className="w-2 h-2 bg-[#6B8FCC] rounded-full animate-pulse" />
          <span className="text-sm font-medium text-[#6B8FCC]">ARCHE Engineering Solutions</span>
        </motion.div>

        {/* Main Headline - Split into lines for animation */}
        <div className="mb-6 overflow-hidden">
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter leading-[1.05]">
            <div className="hero-line">
              <span className="bg-gradient-to-r from-white via-[#B8CDE8] to-white bg-clip-text text-transparent">Engineering Excellence</span>
            </div>
            <div className="hero-line mt-2">
              <span className="bg-gradient-to-r from-[#8FAEE0] via-[#6B8FCC] to-[#B8CDE8] bg-clip-text text-transparent">from Concept to Production</span>
            </div>
          </h1>
        </div>

        {/* Description */}
        <p className="hero-description max-w-4xl mx-auto text-base md:text-lg text-gray-300 leading-relaxed mb-6">
          {heroData.description}
        </p>
        <p className="hero-description max-w-3xl mx-auto text-base md:text-lg text-gray-400 leading-relaxed mb-12">
          {heroData.descriptionExtended}
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Button
            data-testid="hero-request-quote-btn"
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="hero-cta group relative px-8 py-6 text-lg font-semibold bg-gradient-to-r from-[#1E3A6F] to-[#2A4A85] hover:from-[#2A4A85] hover:to-[#4A6FA5] text-white border-0 rounded-xl overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-blue-900/50"
            size="lg"
          >
            <span className="relative z-10 flex items-center gap-2">
              Request a Quote
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </span>
          </Button>

          <Button
            data-testid="hero-explore-services-btn"
            onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
            className="hero-cta group px-8 py-6 text-lg font-semibold bg-white/5 hover:bg-white/10 backdrop-blur-xl border border-white/20 hover:border-[#4A6FA5]/50 text-white rounded-xl transition-all duration-300 hover:scale-105"
            size="lg"
            variant="outline"
          >
            <span className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-[#4A6FA5]/20 transition-colors">
                <Play className="w-4 h-4 fill-current" />
              </div>
              Explore Our Services
            </span>
          </Button>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        >
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <motion.div
              className="w-1 h-2 bg-[#6B8FCC] rounded-full"
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            />
          </div>
        </motion.div>
      </motion.div>

      {/* Floating 3D Elements Indicators */}
      <div className="absolute top-1/4 right-1/4 w-20 h-20 border border-[#4A6FA5]/30 rounded-lg floating-element backdrop-blur-sm" />
      <div className="absolute bottom-1/3 left-1/4 w-16 h-16 border border-[#4A6FA5]/30 rounded-full floating-element backdrop-blur-sm" />
      <div className="absolute top-1/2 right-1/3 w-12 h-12 border border-[#2A4A85]/30 floating-element backdrop-blur-sm" style={{ clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)' }} />
    </div>
  );
}
